import styled from "@emotion/styled";
import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import { Field, useForm } from "react-final-form";
import { FieldArrayProps } from "react-final-form-arrays";
import { useSelector } from "react-redux";

import { Delete, Info } from "@livechat/design-system-icons";
import {
  Accordion,
  Button,
  DesignToken,
  Icon,
  SpacingToken,
  Text,
  TransitionDurationToken,
  TransitionTimingToken,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { ChooseActionOrTriggerModal } from "routes/automations/shared/components/ChooseActionOrTriggerModal";
import { validateTrigger } from "routes/automations/shared/validators";
import { selectAgentsAlphabetically } from "store/entities/agents";
import { selectMailboxesAlphabetically } from "store/entities/mailboxes/selectors";
import {
  IRuleTriggerNewAgentMessage,
  IRuleTriggerNewMessage,
  IRuleTriggerSubject,
} from "store/entities/rules/interfaces";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { OpenedAccordionIndex, TriggerFieldArrayElement, WorkflowConditionType } from "../../interfaces";

import { Trigger } from "./Trigger";
import { TriggerAccordionLabel } from "./TriggerAccordionLabel";
import { TriggerMultilineLabel } from "./TriggerMultilineLabel";
import { TRIGGER_EMPTY_STATE_TEXT, changeTrigger, defaultTriggers, isTriggerType } from "./utils";

interface IProps {
  index: number;
  fields: FieldArrayProps<TriggerFieldArrayElement, HTMLElement>;
  isModalOpen: boolean;
  onModalClose: () => void;
  openedIndex: OpenedAccordionIndex;
  setOpenedIndex: Dispatch<SetStateAction<OpenedAccordionIndex>>;
}

export const TriggerPicker: FC<IProps> = ({
  index,
  fields,
  setOpenedIndex,
  onModalClose,
  isModalOpen,
  openedIndex,
}) => {
  const form = useForm();
  const isOpen = openedIndex?.type === "trigger" && openedIndex.index === index;

  const allTags = useSelector(selectTagsAlphabetically);
  const allTeams = useSelector(selectTeamsAlphabetically);
  const allAgents = useSelector(selectAgentsAlphabetically);
  const allMailboxes = useSelector(selectMailboxesAlphabetically);

  const triggerRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (shouldScroll) {
      triggerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setShouldScroll(false);
    }
  }, [shouldScroll]);

  return (
    <>
      <Field<TriggerFieldArrayElement> name={`triggers.${index}`} key={index}>
        {({ input, meta }) => {
          const handleSelectTrigger = (type: WorkflowConditionType): void => {
            // We want to reset the field state to avoid displaying the error message when the user selects the trigger
            form.resetFieldState(`triggers.${index}`);

            // We want to blur to change touched property in previously opened accordion
            setOpenedIndex(prevIndex => {
              if (prevIndex?.type === "trigger") {
                form.blur(`triggers.${prevIndex.index}`);
              }
              return { type: "trigger", index };
            });

            changeTrigger(input, defaultTriggers[type]);
            setShouldScroll(true);
            onModalClose();
          };

          const handleRemoveTrigger = (): void => {
            fields.remove(index);
            setOpenedIndex(null);
          };

          const handleModalClose = (): void => {
            fields.remove(index);
            onModalClose();
          };

          const handleAccordionOpen = (): void => {
            // We want to blur to change touched property in previously opened accordion
            setOpenedIndex(prevIndex => {
              if (prevIndex?.type === "trigger") {
                form.blur(`triggers.${prevIndex.index}`);
              }
              return { type: "trigger", index };
            });
          };

          const handleAccordionClose = (): void => {
            // We want the touched state to be set to true when the user closes the accordion
            form.blur(`triggers.${index}`);
            setOpenedIndex(null);
          };

          const shouldRenderTile = input.value?.type;

          const shouldDisplayPlaceholder = fields.length === 1 && !shouldRenderTile;

          const shouldOpenModal = !shouldRenderTile && isModalOpen;

          const displayError =
            (meta.touched || !meta.dirty) && validateTrigger(input.value, allTags, allTeams, allAgents, allMailboxes);

          const shouldDisplayMultilineLabel =
            isTriggerType<IRuleTriggerSubject>(input, "subject") ||
            isTriggerType<IRuleTriggerNewMessage>(input, "newMessage") ||
            isTriggerType<IRuleTriggerNewAgentMessage>(input, "newAgentMessage");

          return (
            <>
              <AccordionWrapper ref={triggerRef} visible={!!shouldRenderTile}>
                <Accordion
                  label={{
                    open: <Text bold>Edit condition</Text>,
                    closed: <TriggerAccordionLabel input={input} />,
                  }}
                  multilineElement={
                    shouldDisplayMultilineLabel ? (
                      <TriggerMultilineLabel input={input} onAccordionOpen={handleAccordionOpen} />
                    ) : undefined
                  }
                  isOpen={isOpen}
                  onOpen={handleAccordionOpen}
                  onClose={handleAccordionClose}
                  kind={displayError ? "error" : "default"}
                  css={{ maxWidth: "650px" }}
                >
                  <Block marginLeft={withVar(SpacingToken.Spacing2)}>
                    <Trigger input={input} />
                  </Block>
                  {displayError && (
                    <Flex
                      alignItems="center"
                      gap={withVar(SpacingToken.Spacing1)}
                      marginLeft={withVar(SpacingToken.Spacing1)}
                      marginTop={withVar(SpacingToken.Spacing3)}
                    >
                      <Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicNegative)} />
                      <Text size="sm" customColor={withVar(DesignToken.ContentBasicNegative)}>
                        Make sure the condition is added correctly.
                      </Text>
                    </Flex>
                  )}
                </Accordion>
                <DeleteButton
                  className="delete-button"
                  size="compact"
                  kind="plain"
                  icon={<Icon source={Delete} />}
                  onClick={handleRemoveTrigger}
                  open={isOpen}
                />
              </AccordionWrapper>
              {shouldDisplayPlaceholder && <EmptyStateText size="sm">{TRIGGER_EMPTY_STATE_TEXT}</EmptyStateText>}

              <ChooseActionOrTriggerModal
                mode="add"
                type="trigger"
                onClose={handleModalClose}
                onSelect={handleSelectTrigger}
                isVisible={shouldOpenModal}
              />
            </>
          );
        }}
      </Field>
    </>
  );
};

const AccordionWrapper = styled.div<{ visible: boolean }>`
  display: ${({ visible }): string => (visible ? "flex" : "none")};
  padding-right: ${withVar(SpacingToken.Spacing5)};

  &:hover .delete-button {
    opacity: 1;
    visibility: visible;
  }
`;

const EmptyStateText = styled(Text)`
  margin-bottom: ${withVar(SpacingToken.Spacing3)};
  color: ${withVar(DesignToken.ContentBasicDisabled)};
`;

const DeleteButton = styled(Button)<{ open: boolean }>`
  margin-top: ${withVar(SpacingToken.Spacing4)};
  margin-left: ${withVar(SpacingToken.Spacing3)};
  opacity: ${({ open }): number => (open ? 1 : 0)};
  visibility: ${({ open }): string => (open ? "visible" : "hidden")};
  transition:
    opacity ${withVar(TransitionDurationToken.Fast2)} ${withVar(TransitionTimingToken.EaseOut)},
    visibility ${withVar(TransitionDurationToken.Fast2)} ${withVar(TransitionTimingToken.EaseOut)};
`;
